import React, { useEffect, useState } from "react";

import {
  channelOptions,
  DEFAULT_ERROR_COLOR,
} from "../../utils/composedForecastDisplay";
import type { ChannelOption, SourceDisplaySettings } from "../../utils/composedForecastDisplay";
import { describeForecastOutput, parameterFor } from "../../utils/seriesMetaData";
import { CUSTOM_VIEW_QUICK_COLORS } from "../../utils/colorUtils";
import { customViewModelColor } from "../../utils/composedForecastUtils";

interface SourceDisplayConfigProps {
  display: SourceDisplaySettings | null | undefined;
  onChange: (display: SourceDisplaySettings) => void;
  /** The base model: choose which channels show and their colours. Other sources: one line colour. */
  isBaseModel: boolean;
  /** Output id of this row when it is a model output (the base model's channels come from its seriesMetaData). */
  outputId?: string;
  /** The variable of a multi-variable output this row reads (unset = the first): its channels are the ones offered. */
  variation?: string;
  /** This row is the observation: also offer the error line's colour. */
  isObservation: boolean;
}

// Default swatch for an overlay line — the chart assigns the real default from its palette
const OVERLAY_SWATCH = "#2563eb";

// Quick picks first so views settle on the same colours; the native picker stays for anything else
const ColourInput: React.FC<{
  label: string;
  value: string | undefined;
  fallback: string;
  onChange: (value: string | undefined) => void;
}> = ({ label, value, fallback, onChange }) => (
  <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
    <span role="group" aria-label={`${label} quick picks`} className="inline-flex flex-wrap items-center gap-1">
      {CUSTOM_VIEW_QUICK_COLORS.map(({ name, color }) => {
        const selected = (value || fallback).toLowerCase() === color;
        return (
          <button
            key={color}
            type="button"
            title={name}
            aria-label={`${label}: ${name}`}
            aria-pressed={selected}
            onClick={() => onChange(color)}
            className={`h-4 w-4 shrink-0 rounded-full border border-eai-border transition-transform hover:scale-110 ${
              selected ? "ring-2 ring-eai-ink ring-offset-1" : ""
            }`}
            style={{ backgroundColor: color }}
          />
        );
      })}
    </span>
    <input
      type="color"
      aria-label={label}
      value={value || fallback}
      onChange={(e) => onChange(e.target.value)}
      className="h-6 w-8 cursor-pointer rounded border border-eai-border bg-transparent p-0"
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange(undefined)}
        className="font-inter text-eai-body-xs text-eai-ink-subtle hover:text-eai-ink"
      >
        Reset
      </button>
    )}
  </span>
);

/** How this source is drawn in the composed forecast: shown channels and colours (metadata.display). */
const SourceDisplayConfig: React.FC<SourceDisplayConfigProps> = ({
  display,
  onChange,
  isBaseModel,
  outputId,
  variation,
  isObservation,
}) => {
  const [options, setOptions] = useState<ChannelOption[] | null>(null);
  const settings: SourceDisplaySettings = display || {};

  useEffect(() => {
    setOptions(null);
    if (!isBaseModel || !outputId) return;
    let cancelled = false;
    describeForecastOutput(outputId)
      .then((meta) => {
        // A Custom View draws its main forecast line in the brand olive
        if (!cancelled) setOptions(channelOptions(parameterFor(meta, variation), customViewModelColor()));
      })
      .catch((err) => {
        console.error("Could not describe the forecast output:", err);
        if (!cancelled) setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isBaseModel, outputId, variation]);

  const setChannel = (key: string, patch: { visible?: boolean; color?: string | undefined }) => {
    const current = settings.channels?.[key] || {};
    const next = { ...current, ...patch };
    if (next.visible !== false) delete next.visible;
    if (!next.color) delete next.color;
    const channels = { ...(settings.channels || {}), [key]: next };
    if (!Object.keys(next).length) delete channels[key];
    onChange({ ...settings, channels });
  };

  return (
    <div className="space-y-3">
      <span className="eai-meta-label block">Display</span>

      {isBaseModel ? (
        options === null ? (
          <p className="font-inter text-eai-body-xs text-eai-ink-subtle">
            {outputId ? "Reading the forecast's channels…" : "Pick an output parameter to choose its channels."}
          </p>
        ) : options.length === 0 ? (
          <p className="font-inter text-eai-body-xs text-eai-ink-subtle">This forecast has no channels to configure.</p>
        ) : (
          <ul className="space-y-1.5">
            {options.map((option) => {
              const channel = settings.channels?.[option.key] || {};
              return (
                <li key={option.key} className="flex items-center justify-between gap-3">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={channel.visible !== false}
                      onChange={(e) => setChannel(option.key, { visible: e.target.checked ? undefined : false })}
                    />
                    <span className="font-inter text-eai-body-sm text-eai-ink">{option.label}</span>
                  </label>
                  <ColourInput
                    label={`${option.label} colour`}
                    value={channel.color}
                    fallback={option.defaultColor}
                    onChange={(color) => setChannel(option.key, { color })}
                  />
                </li>
              );
            })}
          </ul>
        )
      ) : (
        <div className="flex items-center justify-between gap-3">
          <span className="font-inter text-eai-body-sm text-eai-ink">Line colour</span>
          <ColourInput
            label="Line colour"
            value={settings.color}
            fallback={OVERLAY_SWATCH}
            onChange={(color) => onChange({ ...settings, color })}
          />
        </div>
      )}

      {isObservation && (
        <div className="flex items-center justify-between gap-3">
          <span className="font-inter text-eai-body-sm text-eai-ink">Error line colour (right axis)</span>
          <ColourInput
            label="Error line colour"
            value={settings.errorColor}
            fallback={DEFAULT_ERROR_COLOR}
            onChange={(errorColor) => onChange({ ...settings, errorColor })}
          />
        </div>
      )}

      {isBaseModel && (
        <p className="font-inter text-eai-body-xs text-eai-ink-subtle">
          Display only changes what is drawn — accuracy still scores the trace or band chosen on the observation.
        </p>
      )}
    </div>
  );
};

export default SourceDisplayConfig;
